<template>
  <div class="page-content">
    <!-- Header -->
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <div v-if="enable" class="d-flex align-center" style="gap: 8px;">
          <h1 class="page-title">{{ typeLabel }} #{{ sale.sale_number }}</h1>
          <v-chip v-if="isReturn" small dark :color="accentColor" class="font-weight-bold">RETOUR</v-chip>
        </div>
        <h1 class="page-title" v-else>Chargement...</h1>
      </div>
      <div class="d-flex align-center" style="gap: 8px;" v-if="enable">
        <v-btn
          v-if="sale.status == 1"
          color="error"
          outlined
          small
          @click="openCancellationDialog"
        >
          <v-icon left small>mdi-cancel</v-icon>
          Annuler
        </v-btn>
        <ExportPDF
          :data="sale"
          :filename="isReturn ? 'fiche_retour' : 'fiche_vente'"
          text="PDF"
          color="primary"
          :outlined="true"
          :is-return="isReturn"
          small
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="!enable" class="page-loader">
      <v-progress-circular indeterminate color="primary" size="40" width="3"></v-progress-circular>
    </div>

    <div v-if="enable">
      <!-- Status banner if cancelled -->
      <v-alert v-if="sale.status == 0" type="error" text dense class="mb-4">
        <div class="d-flex align-center flex-wrap" style="gap: 16px;">
          <strong>{{ typeLabel }} annulé{{ isReturn ? '' : 'e' }}</strong>
          <span class="text-body-2" v-if="sale.canceled_at">
            le {{ sale.canceled_at | formatDate }} à {{ sale.canceled_at | formatTime }}
          </span>
          <span class="text-body-2" v-if="sale.cancellation_user">
            par {{ sale.cancellation_user.full_name }}
          </span>
          <span class="text-body-2" v-if="sale.cancellation_reason">
            — {{ sale.cancellation_reason }}
          </span>
        </div>
      </v-alert>

      <!-- Sale info cards -->
      <v-row class="mb-2">
        <v-col cols="6" sm="3">
          <div class="info-item">
            <div class="info-label">Date</div>
            <div class="info-value">{{ sale.created_at | formatDate }}</div>
            <div class="info-sub">{{ sale.created_at | formatTime }}</div>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="info-item">
            <div class="info-label">Client</div>
            <div class="info-value">{{ sale.customer.full_name }}</div>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="info-item">
            <div class="info-label">Agent</div>
            <div class="info-value">{{ sale.user.full_name }}</div>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="info-item">
            <div class="info-label">Emplacement</div>
            <div class="info-value">{{ sale.store.name }}</div>
          </div>
        </v-col>
      </v-row>

      <!-- Products -->
      <v-card flat class="detail-card mb-4">
        <v-card-title class="detail-card-title">
          <v-icon color="primary" size="18" class="mr-2">mdi-package-variant</v-icon>
          Produits
          <v-spacer></v-spacer>
          <v-chip small outlined color="primary">{{ sale.sale_products.length }} article{{ sale.sale_products.length > 1 ? 's' : '' }}</v-chip>
        </v-card-title>
        <v-simple-table dense>
          <thead>
            <tr>
              <th class="text-left">Produit</th>
              <th class="text-center">Qté</th>
              <th class="text-right">Prix unit.</th>
              <th class="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="product in sale.sale_products" :key="product.id">
              <td class="font-weight-medium">{{ product.product.name }}</td>
              <td class="text-center">
                <v-chip x-small color="primary" dark>{{ product.quantity }}</v-chip>
              </td>
              <td class="text-right">{{ product.price | formatNumber }}</td>
              <td class="text-right font-weight-bold">{{ product.total | formatNumber }}</td>
            </tr>
          </tbody>
        </v-simple-table>

        <!-- Totals -->
        <div class="totals-section">
          <div class="total-row">
            <span>Sous-total</span>
            <span>{{ sale.subtotal | formatNumber }} {{ sale.rate.name }}</span>
          </div>
          <div class="total-row" v-if="sale.discount > 0">
            <span class="error--text">Réduction ({{ sale.discount }}%)</span>
            <span class="error--text">-{{ (sale.subtotal * sale.discount / 100) | formatNumber }} {{ sale.rate.name }}</span>
          </div>
          <div class="total-row" v-if="sale.taxe > 0">
            <span>Taxe</span>
            <span>{{ sale.taxe | formatNumber }}%</span>
          </div>
          <div class="total-row" v-if="sale.service_fee > 0">
            <span>Frais de service</span>
            <span>{{ sale.service_fee | formatNumber }} {{ sale.rate.name }}</span>
          </div>
          <v-divider class="my-2"></v-divider>
          <div class="total-row total-row--grand">
            <span>Total</span>
            <span>{{ sale.total | formatNumber }} {{ sale.rate.name }}</span>
          </div>
          <div class="total-row text-caption" v-if="sale.rate_id != 1 && sale.daily_rate > 0" style="color: #94a3b8;">
            <span>Taux du jour</span>
            <span>{{ sale.daily_rate | formatNumber }}</span>
          </div>
        </div>
      </v-card>

      <!-- Payments -->
      <v-card flat class="detail-card mb-4">
        <v-card-title class="detail-card-title">
          <v-icon color="success" size="18" class="mr-2">mdi-credit-card-outline</v-icon>
          Paiements
        </v-card-title>
        <v-simple-table dense>
          <thead>
            <tr>
              <th class="text-left">Méthode</th>
              <th class="text-right">Montant</th>
              <th class="text-right">Note</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="payment in sale.payments" :key="payment.id">
              <td>
                <div class="d-flex align-center">
                  <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                  {{ payment.method.name }}
                </div>
              </td>
              <td class="text-right font-weight-bold">
                {{ payment.amount | formatNumber }}
                {{ payment.rate_id == 2 ? 'USD' : 'HTG' }}
              </td>
              <td class="text-right text-caption text--secondary">{{ payment.memo || '—' }}</td>
            </tr>
            <tr v-if="sale.change > 0">
              <td>
                <div class="d-flex align-center">
                  <v-icon small color="warning" class="mr-2">mdi-cash-refund</v-icon>
                  Monnaie rendue
                </div>
              </td>
              <td class="text-right font-weight-bold warning--text">
                <span v-if="sale.change_rate_id == 2 && sale.change_rate == sale.rate_id">{{ sale.change | formatNumber }} USD</span>
                <span v-else-if="sale.change_rate_id == 2">{{ (sale.change / sale.daily_rate) | formatNumber }} USD</span>
                <span v-else-if="sale.change_rate_id == 1 && sale.change_rate == sale.rate_id">{{ sale.change | formatNumber }} HTG</span>
                <span v-else>{{ (sale.change * sale.daily_rate) | formatNumber }} HTG</span>
              </td>
              <td class="text-right text-caption text--secondary">—</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>

      <!-- Additional info -->
      <v-card flat class="detail-card mb-4" v-if="sale.memo">
        <v-card-title class="detail-card-title">
          <v-icon color="info" size="18" class="mr-2">mdi-note-text-outline</v-icon>
          Note
        </v-card-title>
        <v-card-text class="text-body-2">{{ sale.memo }}</v-card-text>
      </v-card>
    </div>

    <!-- Cancellation dialog -->
    <v-dialog v-model="cancellationDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">
          Annuler la vente #{{ sale.sale_number }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-alert type="warning" text dense class="mb-4">
            Total : {{ formatNumber(sale.total) }} {{ sale.rate ? sale.rate.name : '' }}
          </v-alert>
          <v-textarea
            v-model="cancellationReason"
            label="Raison de l'annulation"
            placeholder="Expliquez pourquoi..."
            outlined
            dense
            rows="3"
            :error-messages="cancellationReasonError"
            @input="cancellationReasonError = ''"
            autofocus
            hide-details="auto"
          ></v-textarea>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="px-6 py-3">
          <v-spacer></v-spacer>
          <v-btn text @click="closeCancellationDialog" :disabled="cancellationLoading">Fermer</v-btn>
          <v-btn color="error" depressed @click="confirmCancellation" :loading="cancellationLoading">
            Confirmer l'annulation
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" bottom right>
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import ExportPDF from '../components/Tools/ExportPDF.vue'

export default {
  name: 'SaleDetailsView',
  components: { BreadcrumbComponent, ExportPDF },

  computed: {
    isReturn() {
      return this.enable && this.sale && parseFloat(this.sale.total) < 0
    },
    accentColor() {
      return this.isReturn ? '#f5843b' : '#194173'
    },
    typeLabel() {
      return this.isReturn ? 'Retour' : 'Vente'
    }
  },

  data: () => ({
    items: [{ text: 'Ventes', disabled: false, href: '/sales' }],
    enable: false,
    sale: [],
    cancellationDialog: false,
    cancellationReason: '',
    cancellationReasonError: '',
    cancellationLoading: false,
    snackbar: { show: false, text: '', color: 'success', timeout: 3000 }
  }),

  methods: {
    formatNumber(value) {
      return new Intl.NumberFormat('fr-FR').format(value)
    },

    load() {
      var self = this
      this.$axios.get("/sales/" + this.$route.params.id + "?include=store,device,sale_products.product,user,customer,rate,payments.method,cancellation_user")
        .then(function (response) {
          self.sale = response.data
          self.enable = true
          const isRet = parseFloat(response.data.total) < 0
          self.items = [
            { text: isRet ? 'Retours' : 'Ventes', disabled: false, href: isRet ? '/returns' : '/sales' },
            { text: '#' + response.data.sale_number, disabled: true, href: '#' },
          ]
        })
        .catch(function (error) {
          console.error('Erreur:', error)
          self.showSnackbar('Erreur lors du chargement', 'error')
        })
    },

    openCancellationDialog() {
      if (this.sale.status == 1) {
        this.cancellationDialog = true
        this.cancellationReason = ''
        this.cancellationReasonError = ''
      } else {
        this.showSnackbar('Cette vente est déjà annulée', 'warning')
      }
    },

    closeCancellationDialog() {
      if (!this.cancellationLoading) {
        this.cancellationDialog = false
        this.cancellationReason = ''
        this.cancellationReasonError = ''
      }
    },

    confirmCancellation() {
      var self = this

      if (!this.cancellationReason || this.cancellationReason.trim() === '') {
        this.cancellationReasonError = "La raison de l'annulation ne peut pas être vide"
        return
      }

      this.cancellationLoading = true

      var userId = 1
      try {
        const auth = JSON.parse(localStorage.authentication)
        if (auth && auth.user && auth.user.id) userId = auth.user.id
      } catch (e) { /* keep default */ }

      const now = new Date()
      const formattedDate = now.getFullYear() + '-' +
        String(now.getMonth() + 1).padStart(2, '0') + '-' +
        String(now.getDate()).padStart(2, '0') + ' ' +
        String(now.getHours()).padStart(2, '0') + ':' +
        String(now.getMinutes()).padStart(2, '0') + ':' +
        String(now.getSeconds()).padStart(2, '0')

      const saleData = {
        sale_number: this.sale.sale_number,
        change: this.sale.change,
        change_rate: this.sale.change_rate,
        change_rate_id: this.sale.change_rate_id,
        created_at: this.sale.created_at,
        customer_id: this.sale.customer_id,
        daily_rate: this.sale.daily_rate,
        device_id: this.sale.device_id,
        discount: this.sale.discount,
        rate_id: this.sale.rate_id,
        payments: this.sale.payments,
        sale_products: this.sale.sale_products,
        status: 0,
        store_id: this.sale.store_id,
        subtotal: this.sale.subtotal,
        taxe: this.sale.taxe,
        total: this.sale.total,
        user_id: this.sale.user_id,
        memo: this.sale.memo || null,
        cancellation_reason: this.cancellationReason.trim(),
        canceled_by: userId,
        canceled_at: formattedDate
      }

      this.$axios.post('/sales', saleData)
        .then(function () {
          self.cancellationLoading = false
          self.cancellationDialog = false
          self.showSnackbar('Vente annulée avec succès', 'success')
          self.load()
        })
        .catch(function (error) {
          self.cancellationLoading = false
          let msg = "Erreur lors de l'annulation"
          if (error.response?.data?.message) msg = error.response.data.message
          self.showSnackbar(msg, 'error')
        })
    },

    showSnackbar(text, color = 'success') {
      this.snackbar.text = text
      this.snackbar.color = color
      this.snackbar.show = true
    }
  },

  mounted() { this.load() },
}
</script>

<style scoped>
/* Info items (top row) */
.info-item {
  background: #fff;
  border: 1px solid #e8ecf1;
  border-radius: 8px;
  padding: 14px 16px;
  height: 100%;
}

.info-label {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
  margin-bottom: 4px;
}

.info-value {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1a2332;
  line-height: 1.3;
}

.info-sub {
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 2px;
}

/* Detail cards */
.detail-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 10px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
  overflow: hidden;
}

.detail-card-title {
  font-size: 0.875rem !important;
  font-weight: 600 !important;
  padding: 14px 16px !important;
  color: #1a2332;
  border-bottom: 1px solid #f1f5f9;
}

/* Tables */
.detail-card .v-simple-table {
  border: none !important;
  box-shadow: none !important;
}

.detail-card .v-simple-table thead th {
  font-size: 0.6875rem !important;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8 !important;
  background: transparent !important;
  border-bottom: 1px solid #e8ecf1 !important;
  padding: 8px 16px !important;
}

.detail-card .v-simple-table tbody td {
  font-size: 0.8125rem;
  padding: 12px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.detail-card .v-simple-table tbody tr:last-child td {
  border-bottom: none !important;
}

.detail-card .v-simple-table tbody tr:hover {
  background: rgba(25, 65, 115, 0.02) !important;
}

/* Totals section */
.totals-section {
  padding: 16px 16px 20px;
  background: #f8fafc;
  border-top: 1px solid #e8ecf1;
}

.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  font-size: 0.875rem;
  color: #475569;
}

.total-row--grand {
  font-size: 1.125rem;
  font-weight: 700;
  color: #194173;
  padding: 6px 0;
}
</style>
